/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation, Trans } from 'react-i18next';

import Button from '@mui/material/Button';
import { Typography as Text } from '@mui/material';

import { useTranscriptUploadActions } from '../../actions/FileActions';

import FileRecorder from './FileRecorder';
import UploadProgress from './UploadProgress';
import UploadSettings from './UploadSettings';
import { confirmDialog } from '../../utils/dialog';
import { getAudioDevices, requestPermissions } from '../../utils/media';
import { RecordingsDB } from '../../utils/db_utils';
import { IS_SINGLE_TENENT_MODE } from '../../constants';

import MicOffIcon from '@mui/icons-material/MicOff';
import MicIcon from '@mui/icons-material/Mic';
import WarningIcon from '@mui/icons-material/WarningOutlined';

import DialogHeader from './DialogHeader';

const recordingsDB = new RecordingsDB();

export default function UploadRecord({
  handleClose,
  setUploading,
  exceedsUploadLimit,
}) {
  const { t } = useTranslation();

  const { uploadTranscriptAsync, uploadError } = useTranscriptUploadActions();

  const [step, setStep] = useState(0);
  const [supportsBlob, setSupportsBlob] = useState(true);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('default');
  const [existingRecording, setExistingRecording] = useState(false);
  const [settings, setSettings] = useState({
    numSpeakers: 0,
    language: 'auto',
    outputFormat: 'transcript',
    transcribeMode: 0,
    name: new Date().toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
  });
  const [file, setFile] = useState(null);
  const [fileProgress, setFileProgress] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [fileSize, setFileSize] = useState(0);
  const wakeLock = useRef(null);

  const uploadDone = fileProgress?.progress >= 100;

  const preferredAudioDevice = window.localStorage.getItem(
    'preferredAudioDevice'
  );

  useEffect(() => {
    recordingsDB.checkBlobSupport().then((result) => {
      setSupportsBlob(result);
    });
  }, [supportsBlob]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const blobs = await recordingsDB.getAllBlobsFromIndexedDB();
        if (blobs.length > 0) {
          let settings = await recordingsDB.getSettingsFromIndexedDB();
          setExistingRecording(settings);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const initializeAudioDevices = async () => {
      try {
        await requestPermissions(preferredAudioDevice);
        const devices = await getAudioDevices();
        setAudioDevices(devices);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setError(error.message);
      }
    };

    initializeAudioDevices();
  }, []);

  useEffect(() => {
    if (audioDevices.length > 0) {
      setSelectedAudioDevice(preferredAudioDevice || audioDevices[0].deviceId);
    }
  }, [audioDevices]);

  useEffect(() => {
    if (step >= 1) {
      setUploading(true);
    } else {
      setUploading(false);
    }

    return () => {
      setUploading(false);
    };
  }, [step, setUploading]);

  useEffect(() => {
    if (uploadDone || uploadError) {
      setUploading(false);
    }
  }, [uploadDone, uploadError, setUploading]);

  const handleUpload = useCallback(async () => {
    let restoredSettings = settings;
    if (!restoredSettings?.name || !restoredSettings?.folderPrefix) {
      restoredSettings = await recordingsDB.getSettingsFromIndexedDB();
      if (!restoredSettings?.name || !restoredSettings?.folderPrefix) {
        return false;
      }
    }

    setFileProgress({ name: restoredSettings.name, progress: 0 });

    let blobs = await recordingsDB.getAllBlobsFromIndexedDB();
    if (blobs.length > 0) {
      const form = new FormData();
      let blob = await recordingsDB.concatenateBlobs(blobs, 'audio/webm');
      form.append('file', blob, 'recording.webm');

      const size = blob.size;
      setFileSize(size);

      const params = {
        ...restoredSettings,
      };

      setStep(1);

      try {
        const response = await uploadTranscriptAsync({
          form,
          ...params,
          recording: true,
          url: 'recording',
          onProgress: ({ progress }) => {
            setFileProgress({ name: settings.name, progress: progress * 100 });
          },
        });
        try {
          if (!IS_SINGLE_TENENT_MODE) {
            await downloadDB();
          }
        } catch (error) {
          console.error('Could not trigger download', error);
        }
        if (response.meeting && response.meeting?.id) {
          await recordingsDB.clearObjectStore();
        }
      } catch (error) {
        console.error('Could not upload file', error);
        try {
          if (!IS_SINGLE_TENENT_MODE) {
            await downloadDB();
          }
        } catch (error) {
          console.error('Could not trigger download', error);
        }
      }
    }
  }, [settings, file, uploadTranscriptAsync]);

  if (loading) {
    return (
      <React.Fragment>
        <div
          css={css`
            display: flex;
            justify-content: center;
            flex-direction: column;

            align-items: center;
            height: 100%;

            margin-bottom: 1rem;
            gap: 1rem;
          `}
        >
          <div
            css={css`
              display: flex;
            `}
          >
            <div
              css={css`
                background-color: rgba(0, 0, 0, 0.12);
                border-radius: 50%;
                width: 40px;
                height: 40px;

                display: flex;
                justify-content: center;
                align-items: center;
              `}
            >
              <MicOffIcon
                fontSize="medium"
                css={css`
                  color: rgba(0, 0, 0, 0.54);
                  padding: 0;
                `}
              />
            </div>
            <WarningIcon
              fontSize="small"
              color="warning"
              css={css`
                position: relative;
                left: -14px;
                top: -5px;
              `}
            />
          </div>
          <Text variant="subtitle1" fontWeight="600">
            {t(
              'Vi behöver din tillåtelse för att kunna spela in från din mikrofon.'
            )}
          </Text>
          {!error ? (
            <Text variant="subtitle2" color="text.secondary" fontWeight="600">
              {t('Mikrofonen används endast för inspelning och lagras inte.')}
            </Text>
          ) : (
            <Text
              variant="subtitle2"
              color="text.secondary"
              fontWeight="600"
              css={css`
                text-align: center;
              `}
            >
              {error}
            </Text>
          )}
        </div>
      </React.Fragment>
    );
  }

  async function clearDB() {
    const ok = await confirmDialog({
      title: t('Ta bort inspelningen?'),
      content: t('Är du säker på att du vill ta bort inspelningen?'),
    });

    if (ok) {
      try {
        await recordingsDB.clearObjectStore();
        setExistingRecording(false);
      } catch (err) {
        console.error(err);
      }
    }
    return ok;
  }
  async function downloadDB() {
    // restore file from db and trigger download
    let blobs = await recordingsDB.getAllBlobsFromIndexedDB();
    if (blobs.length > 0) {
      const fileName = `klangai-recording-${new Date().toISOString()}.webm`;

      const blob = await recordingsDB.concatenateBlobs(blobs, 'audio/webm');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
    }
  }

  async function onFileReset() {
    setExistingRecording(false);
  }

  if (existingRecording && step === 0) {
    return (
      <React.Fragment>
        <DialogHeader
          title={t('Avbruten diktafoninspelning')}
          onClose={handleClose}
        />
        <div>
          <Text>
            <Trans t={t}>
              Filen <b>"{{ name: existingRecording.name }}"</b> är påbörjad men
              ännu inte uppladdad.
            </Trans>
          </Text>
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              gap: 1rem;
              margin-top: 1rem;
              margin-bottom: 1rem;
            `}
          >
            <Button variant="outlined" color="danger" onClick={() => clearDB()}>
              {t('Ta bort')}
            </Button>
            <div>
              {!IS_SINGLE_TENENT_MODE && (
                <Button
                  variant="outlined"
                  onClick={() => downloadDB()}
                  style={{ marginRight: '5px' }}
                >
                  {t('Ladda ner')}
                </Button>
              )}
              <Button variant="contained" onClick={() => handleUpload()}>
                {t('Ladda upp')}
              </Button>
            </div>
          </div>
          <UploadSettings
            settings={settings}
            setSettings={setSettings}
            disabled={exceedsUploadLimit}
            nameEdit
          />
        </div>
      </React.Fragment>
    );
  }

  if (!supportsBlob) {
    return (
      <div>
        <DialogHeader
          title={t('Diktafon')}
          icon={MicIcon}
          onClose={handleClose}
        />
        <Text variant="subtitle2" color="text.secondary">
          {t('Din webbläsare stödjer inte inspelning.')}
        </Text>
      </div>
    );
  }

  return (
    <div>
      {step === 0 && (
        <React.Fragment>
          <DialogHeader
            title={t('Diktafon')}
            icon={MicIcon}
            onClose={handleClose}
          />
          <FileRecorder
            selectedAudioDevice={selectedAudioDevice}
            file={file}
            onFile={(file) => {
              setFile(file);
            }}
            settings={settings}
            wakeLock={wakeLock}
            audioDevices={audioDevices}
            onAudioDeviceChange={setSelectedAudioDevice}
            exceedsUploadLimit={exceedsUploadLimit}
            setSettings={setSettings}
            onUpload={() => handleUpload()}
            onReset={onFileReset}
          />
        </React.Fragment>
      )}
      {step === 1 && (
        <UploadProgress
          progress={fileProgress?.progress}
          fileSize={fileSize}
          fileProgress={[fileProgress]}
          uploadDone={uploadDone}
          uploadError={uploadError}
          onClose={handleClose}
          fileName={settings.name}
          files={[
            {
              name: settings.name,
              progress: fileProgress.progress,
              size: fileSize,
              type: '.webm',
            },
          ]}
          multi={true}
        />
      )}
    </div>
  );
}
